import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { GOOGLE_FONTS, FONT_CATEGORIES } from '@/lib/fonts';

export function FontSearchPicker({ value, onChange, label, isOptional, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Load selected font for preview
  useEffect(() => {
    if (!value) return;
    const id = `gfont-${value.replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(value)}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [value]);

  const query = search.toLowerCase();

  const filteredCategories = Object.entries(FONT_CATEGORIES).map(([cat, fonts]) => ({
    category: cat,
    fonts: query ? fonts.filter(f => f.toLowerCase().includes(query)) : fonts,
  })).filter(g => g.fonts.length > 0);

  const totalResults = filteredCategories.reduce((n, g) => n + g.fonts.length, 0);

  return (
    <div className={`relative ${className || ''}`} ref={dropdownRef}>
      {label && (
        <label className={`block text-xs mb-1.5 ${isOptional ? 'text-[var(--orbis-t4)]' : 'text-[var(--orbis-t3)]'}`}>
          {label} {isOptional && <span className="text-[var(--orbis-t4)]">(optional)</span>}
        </label>
      )}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-[var(--orbis-bg)] border border-[var(--orbis-s2)] text-[12px] text-[var(--orbis-t2)] hover:border-[var(--orbis-s3)] transition-colors cursor-pointer"
        style={{ fontFamily: 'var(--orbis-font)' }}
      >
        <span className="truncate" style={{ fontFamily: value ? `"${value}", sans-serif` : 'inherit', color: value ? 'var(--orbis-t1)' : 'var(--orbis-t4)' }}>
          {value || 'Select font...'}
        </span>
        <ChevronDown className={`w-3 h-3 text-[var(--orbis-t4)] transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--orbis-s1)] border border-[var(--orbis-s2)] rounded-lg shadow-xl overflow-hidden"
             style={{ maxHeight: '250px', display: 'flex', flexDirection: 'column' }}>
          {/* Search bar */}
          <div className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--orbis-s2)' }}>
            <Input
              placeholder={`Search ${GOOGLE_FONTS.length} fonts...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 text-xs bg-[var(--orbis-bg)] border-[var(--orbis-s2)] text-[var(--orbis-t1)]"
              autoFocus
            />
          </div>
          {/* Font list grouped by category */}
          <div className="p-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {value && !search && (
              <button
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className="w-full text-left px-3 py-1.5 text-xs text-[var(--orbis-t4)] hover:bg-[var(--orbis-s2)] rounded"
              >
                Clear selection
              </button>
            )}
            {filteredCategories.map(({ category, fonts }) => (
              <div key={category}>
                <div className="px-3 pt-2.5 pb-1 flex items-center justify-between">
                  <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-s3)' }}>
                    {category}
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--orbis-s2)' }}>{fonts.length}</span>
                </div>
                {fonts.map(font => (
                  <button
                    key={font}
                    onClick={() => { onChange(font); setOpen(false); setSearch(''); }}
                    onMouseEnter={() => {
                      const id = `gfont-${font.replace(/\s+/g, '-')}`;
                      if (document.getElementById(id)) return;
                      const link = document.createElement('link');
                      link.id = id;
                      link.rel = 'stylesheet';
                      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;700&display=swap`;
                      document.head.appendChild(link);
                    }}
                    className={`w-full text-left px-3 py-1.5 rounded transition-colors flex items-center justify-between ${
                      font === value
                        ? 'bg-[var(--orbis-s2)] text-[var(--orbis-t1)]'
                        : 'text-[var(--orbis-t3)] hover:bg-[var(--orbis-s2)] hover:text-[var(--orbis-t1)]'
                    }`}
                  >
                    <span className="text-sm truncate" style={{ fontFamily: `"${font}", sans-serif` }}>
                      {font}
                    </span>
                    {font === value && <Check className="w-3 h-3 shrink-0 ml-2" style={{ color: 'var(--orbis-blue)' }} />}
                  </button>
                ))}
              </div>
            ))}
            {totalResults === 0 && (
              <div className="px-3 py-4 text-center text-xs text-[var(--orbis-t4)]">
                No fonts match "{search}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
